import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useExperiences } from "../../context/ExperiencesContext";
import ExperiencesAPI from "../../services/ExperiencesAPI";
import GuideOptionsDropdown from "../GuideOptionsDropdown";
import SystemOptionsDropdown from "../SystemOptionsDropdown";
import { AppButton, AppTextField } from "../ui";

const buildEditForm = (experience) => ({
  name: experience?.name || experience?.metadata?.name || "",
  systemId: experience?.systemId || experience?.metadata?.systemId || "",
  guideId: experience?.guideId || experience?.metadata?.guideId || "",
});

const EditExperienceDialog = ({
  open,
  experience,
  onClose,
  onSaved,
  title = "Edit Experience",
}) => {
  const { fetchData } = useExperiences();
  const [form, setForm] = useState(buildEditForm(experience));
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(buildEditForm(experience));
    setErrorMessage("");
  }, [experience, open]);

  const handleClose = () => {
    if (isSaving) {
      return;
    }

    onClose?.();
  };

  const handleSave = async () => {
    const trimmedName = form.name.trim();
    const experienceId = experience?.experienceId || experience?.id || "";

    if (!experienceId || !trimmedName || !form.systemId || !form.guideId) {
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    const updatedExperience = {
      ...experience,
      experienceId,
      name: trimmedName,
      systemId: form.systemId,
      guideId: form.guideId,
    };

    try {
      await ExperiencesAPI.post("experiences", updatedExperience, {
        requiresAuth: true,
      });
      await fetchData();
      onSaved?.(updatedExperience);
      onClose?.();
    } catch (error) {
      console.error("Error updating experience:", error);
      setErrorMessage(
        error?.response?.data?.message || "Unable to update this experience right now."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const isSubmitDisabled =
    isSaving || !form.name.trim() || !form.systemId || !form.guideId;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: "grid", gap: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <AppTextField
            label="Experience Name"
            value={form.name}
            onChange={(event) =>
              setForm((previousForm) => ({
                ...previousForm,
                name: event.target.value,
              }))
            }
            fullWidth
          />
          <SystemOptionsDropdown
            value={form.systemId}
            onChange={(systemId) =>
              setForm((previousForm) => ({
                ...previousForm,
                systemId,
              }))
            }
          />
          <GuideOptionsDropdown
            value={form.guideId}
            onChange={(guideId) =>
              setForm((previousForm) => ({
                ...previousForm,
                guideId,
              }))
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <AppButton kind="ghost" onClick={handleClose} disabled={isSaving}>
          Cancel
        </AppButton>
        <AppButton
          onClick={handleSave}
          kind="primary"
          disabled={isSubmitDisabled}
          startIcon={isSaving ? <CircularProgress size={16} /> : null}
        >
          {isSaving ? "Saving..." : "Save"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditExperienceDialog;
